import { Flex } from "@redotech/arbiter/flex";
import { RedoModal } from "@redotech/arbiter/modal/redo-modal";
import { Text } from "@redotech/arbiter/text";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import {
  getStorefrontPublishStatus,
  isScriptTagDeprecated,
  StorefrontProductKey,
  StorefrontPublishStatus,
} from "@redotech/redo-team-common/storefront-publish";
import {
  StorefrontPublishStatusBadge,
  StorefrontPublishStatusControl,
} from "@redotech/redo-web/storefront-publish-status-control";
import { memo, useState } from "react";
import { RedoAdminClientContext } from "../../../client/context";
import {
  getAppEmbedInfo,
  getTeam,
  updateScriptTag,
  updateTeam,
} from "../../../client/team";

/**
 * Self-contained publish-status control for a single product, intended for
 * product settings pages (coverage builder, returns settings). Shows the
 * current status as a badge plus a three-way dropdown, persists changes through
 * `updateTeam`, and — when the merchant turns a product on while neither the
 * app embed nor the script tag is loading Redo — prompts to turn on the script
 * tag so the change actually takes effect on the storefront.
 */
export const StorefrontPublishConfigurator = memo(
  function StorefrontPublishConfigurator({
    teamId,
    productKey,
    label = "Publish status",
    onSaved,
  }: {
    teamId: string;
    productKey: StorefrontProductKey;
    label?: string;
    onSaved?: () => void;
  }) {
    const client = useRequiredContext(RedoAdminClientContext);
    const [status, setStatus] = useState<StorefrontPublishStatus | undefined>(
      undefined,
    );
    const [pending, setPending] = useState(false);
    const [scriptTagPromptOpen, setScriptTagPromptOpen] = useState(false);
    const [scriptTagPending, setScriptTagPending] = useState(false);
    // App embed / script tag state, tracked locally so we can reflect a
    // just-enabled script tag without a full reload.
    const [appEmbedEnabled, setAppEmbedEnabled] = useState(false);
    const [scriptTagEnabled, setScriptTagEnabled] = useState(false);

    // Load the current publish status and the app embed / script tag state.
    useLoad(
      async (signal) => {
        const [team, appEmbedInfo] = await Promise.all([
          getTeam(client, { teamId, signal }),
          getAppEmbedInfo(client, { teamId, signal }).catch(() => undefined),
        ]);
        setStatus(
          getStorefrontPublishStatus(
            team.settings.storefrontPublishStatus,
            productKey,
          ),
        );
        setAppEmbedEnabled(!!appEmbedInfo?.appEmbedEnabled);
        setScriptTagEnabled(!!appEmbedInfo?.scriptTagEnabled);
      },
      [teamId, productKey],
    );

    const nothingLive = !appEmbedEnabled && !scriptTagEnabled;
    const scriptTagDeprecated = isScriptTagDeprecated();

    const persist = useHandler(async (next: StorefrontPublishStatus) => {
      setPending(true);
      try {
        const team = await getTeam(client, { teamId });
        team.settings.storefrontPublishStatus = {
          ...team.settings.storefrontPublishStatus,
          [productKey]: next,
        };
        await updateTeam(client, { teamId, team });
        setStatus(next);
        onSaved?.();
      } finally {
        setPending(false);
      }
    });

    const onChange = useHandler(async (next: StorefrontPublishStatus) => {
      await persist(next);
      // Enabling a product when nothing is loading Redo won't take effect until
      // the script tag (or app embed) is on — offer to turn the script tag on.
      const enabling =
        next === StorefrontPublishStatus.LIVE_SITE ||
        next === StorefrontPublishStatus.DRAFT_THEMES;
      if (enabling && nothingLive) {
        setScriptTagPromptOpen(true);
      }
    });

    const enableScriptTag = useHandler(async () => {
      setScriptTagPending(true);
      try {
        await updateScriptTag(client, { teamId, enabled: true });
        setScriptTagEnabled(true);
        setScriptTagPromptOpen(false);
      } finally {
        setScriptTagPending(false);
      }
    });

    if (status === undefined) {
      return null;
    }

    return (
      <>
        <Flex align="center" gap="md">
          <Text fontSize="sm" fontWeight="medium">
            {label}
          </Text>
          <StorefrontPublishStatusBadge status={status} />
          <StorefrontPublishStatusControl
            disabled={pending}
            onChange={onChange}
            productKey={productKey}
            value={status}
          />
        </Flex>
        {scriptTagPromptOpen && (
          <RedoModal
            isOpen={scriptTagPromptOpen}
            onModalCloseRequested={() => setScriptTagPromptOpen(false)}
            primaryButton={{
              text: scriptTagDeprecated ? "Got it" : "Turn on script tag",
              onClick: scriptTagDeprecated
                ? () => setScriptTagPromptOpen(false)
                : enableScriptTag,
              pending: scriptTagDeprecated ? undefined : scriptTagPending,
            }}
            secondaryButton={
              scriptTagDeprecated
                ? undefined
                : {
                    text: "Not now",
                    onClick: () => setScriptTagPromptOpen(false),
                  }
            }
            subtitle={
              scriptTagDeprecated
                ? "Shopify no longer allows script tags to be enabled. Turn on the app embed on the Storefront publishing page so this change takes effect."
                : "Script tag will be going away after October 1st due to Shopify limitations and you will have to add the code with app embed. Enable it temporarily so your change takes effect?"
            }
            title="Redo isn't loading on this store"
          />
        )}
      </>
    );
  },
);
